import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { CsvServiceService } from '../../../../../services/csv/csv-service.service';
import { SPREADSHEET_ACCEPT, isSpreadsheetFile } from '../../../../../../utils/utils';
import { formatCount } from '../../../../../../utils/sms';

@Component({
  selector: 'csv-upload-file',
  standalone: true,
  imports: [],
  templateUrl: './upload-file.component.html',
  styleUrl: './upload-file.component.css',
})
export class UploadFileComponent {
  router = inject(Router);
  csvService = inject(CsvServiceService);

  fileName: string = '';
  loading: boolean = false;
  tableHeaders: string[] = [];
  tableData: string[][] = [];
  /** Sheet row number of each entry in tableData. */
  private rowNumbers: number[] = [];
  isHovering: boolean = false;
  /** Why the file could not be used, shown under the drop area. */
  errorMsg: string = '';
  readonly acceptedFormats = SPREADSHEET_ACCEPT;
  readonly formatCount = formatCount;

  /** The first rows, to check the file looks right before mapping. */
  get previewRows(): string[][] {
    return this.tableData.slice(0, 5);
  }

  get hasFile(): boolean {
    return !!this.fileName && !this.loading && !this.errorMsg && this.tableData.length > 0;
  }

  /** What the status region says: reading, then what was found. Errors use their own alert. */
  get readStatus(): string {
    if (this.loading) return `Reading ${this.fileName}`;
    if (!this.hasFile) return '';
    const rows = this.tableData.length;
    return `${this.fileName}: ${formatCount(rows)} ${rows === 1 ? 'contact row' : 'contact rows'}, ${
      this.tableHeaders.length
    } ${this.tableHeaders.length === 1 ? 'column' : 'columns'}.`;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isHovering = true;
    // console.log('Drag Over Event:', event);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isHovering = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isHovering = false;
    if (event.dataTransfer?.files?.[0]) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      this.handleFile(input.files[0]);
    }
    // Choosing the same file again should still trigger a change.
    input.value = '';
  }

  private handleFile(file: File) {
    this.fileName = file.name;
    this.errorMsg = '';
    if (!isSpreadsheetFile(file.name)) {
      this.errorMsg = `${file.name} is not a spreadsheet. Choose a .csv, .xls or .xlsx file.`;
      this.tableHeaders = [];
      this.tableData = [];
      this.rowNumbers = [];
      return;
    }
    this.loading = true;
    // Defer the heavy parse off the click stack so the loading spinner paints.
    setTimeout(() => this.parseFile(file), 0);
  }

  // Unified parser for CSV / XLS / XLSX. xlsx handles quoted fields with embedded
  // commas and newlines, escaped quotes, and UTF-8 correctly — the previous
  // byte-chunked split(',')/split('\n') parser broke on all three.
  private parseFile(file: File): void {
    const reader = new FileReader();
    reader.onerror = () => {
      this.loading = false;
      this.errorMsg = 'The file could not be read. Save it again from your spreadsheet app and retry.';
    };
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, {
          type: 'array',
          // Skip styles/formulas — we only need cell values.
          cellFormula: false,
          cellHTML: false,
          cellStyles: false,
        });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        // Rows keyed by column letter ("A", "B"…): unlike header:1 arrays,
        // these carry their sheet row (__rowNum__, 0-based), so the import
        // tables can say "Row 14" for row 14 of the file even when blank rows
        // were skipped.
        const range = worksheet?.['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null;
        const letters: string[] = [];
        if (range) {
          for (let c = range.s.c; c <= range.e.c; c++) letters.push(XLSX.utils.encode_col(c));
        }
        const lines = (range
          ? XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
              header: 'A',
              defval: '',
              blankrows: false,
              raw: false, // formatted strings, so dates come out as user-visible text
            })
          : []
        )
          .map((line) => ({
            cells: letters.map((letter) => (line[letter] ?? '').toString()),
            sheetRow: Number(line['__rowNum__']) + 1,
          }))
          // blankrows:false already drops empty rows; also drop whitespace-only ones.
          .filter((line) => line.cells.some((cell) => cell.trim() !== ''));
        if (!lines.length) {
          this.tableHeaders = [];
          this.tableData = [];
          this.rowNumbers = [];
          this.loading = false;
          this.errorMsg = 'This file is empty. Add a header row and at least one contact, then choose it again.';
          return;
        }
        this.tableHeaders = lines[0].cells.map((h) => h.trim());
        const body = lines.slice(1);
        this.tableData = body.map((line) => line.cells);
        this.rowNumbers = body.map((line, i) => (Number.isFinite(line.sheetRow) ? line.sheetRow : i + 2));
        this.loading = false;
        if (!this.tableData.length) {
          this.errorMsg = 'The file has a header row but no contacts under it. Add rows, then choose it again.';
        }
      } catch (error) {
        this.loading = false;
        this.errorMsg = 'The file could not be read. Save it again as CSV or Excel and retry.';
      }
    };
    reader.readAsArrayBuffer(file);
  }

  // goes to the next step and sends the csv gleaned files to the csv service
  continue() {
    this.csvService.updateTableHeaders(this.tableHeaders);
    this.csvService.updateTableData(this.tableData);
    this.csvService.updateTableRowNumbers(this.rowNumbers);
    this.csvService.generateObjectifiedData(this.tableHeaders, this.tableData);
    this.router.navigate(['/organization/contacts/csv'], {
      queryParams: { title: 'mapping' },
    });
  }

  close() {
    this.router.navigate(['/organization/contacts']);
  }
}
